import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { shallowEqual } from "react-redux";
import i18next from "src/i18n/config";
import { PermissionService } from "src/services/permissionService/permissionService";
import { RootState, useSelector } from "src/store";
import { FileUploadModal } from "src/ui/containers/dashboard/files/components/FileUploadModal/FileUploadModal";

import { GroupTree } from "@dashboard/files/components/GroupTree/GroupTree";
import { UploadProgressBar } from "@dashboard/files/components/UploadProgressBar";
import UploadTable from "@dashboard/files/components/UploadTable/UploadTable";
import { selectGroupChildrenIds, selectGroups } from "@dashboard/groups/store/selectors";
import { PageContainer } from "@dashboard/shared/components/PageContainer";
import PageHeader from "@dashboard/shared/components/PageHeader";
import { FileExplorerHeader } from "@dashboard/shared/components/SidePanel/shared/FileExplorerHeader";
import { useCurrentWorkspace, useIsWorkspaceFeatureLimitReached } from "@dashboard/workspaces/api/helpers";
import { withMetaDescription } from "@navigation/utils/withMetaDescription";
import AuthService from "@services/authService";
import { ExpiredWrapper } from "@shared/ExpiredWrapper";

import { UploadProvider, useUploadContext } from "./context/UploadContext";
import { useFilePageTree } from "./hooks/useFilePageTree";
import { useStyles } from "./UploadPage.style";

type UploadTarget = { groupId: number | undefined; groupName: string | undefined; isWorkspace: boolean };

const getUploadTarget = (params: {
    selectedGroupId: number | null;
    groupsToUpload: API.Group[];
    groups: API.Group[];
}): UploadTarget => {
    const { selectedGroupId, groups } = params;

    if (selectedGroupId !== null) {
        const group = groups.find((g) => g.id === selectedGroupId);
        return { groupId: selectedGroupId, groupName: group?.name, isWorkspace: false };
    }

    return { groupId: undefined, groupName: undefined, isWorkspace: true };
};

const UploadPageContent = () => {
    const { classes } = useStyles();
    const { data: workspace } = useCurrentWorkspace();
    const { t } = useTranslation(["common", "pages", "modals"]);

    const [userSelectedGroupId, setUserSelectedGroupId] = useState<number | null | undefined>(undefined);
    const [includeParentFiles, setIncludeParentFiles] = useState(true);
    const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
    const { isOpen: treeOpen, onToggle: handleTreeToggle } = useFilePageTree();

    const { workspaceAbility } = PermissionService();
    const { uploadStarted } = useUploadContext();

    // Close the upload dialog when upload starts to show only the page-level progress bar
    useEffect(() => {
        if (uploadStarted) {
            setUploadDialogOpen(false);
        }
    }, [uploadStarted]);

    const handleGroupSelect = (groupId: number | null) => {
        setUserSelectedGroupId(groupId);
    };
    const cannotUploadFile = workspaceAbility(workspace).cannot("manage", "File");
    const groups = useSelector(selectGroups);

    const currentUser = AuthService.getCurrentUser()?.email;

    const userGroups = groups.filter((group) => group.members.some((member) => member.email === currentUser));
    const nestedGroupIds = useSelector(
        (state: RootState) => Array.from(new Set(userGroups.flatMap((group) => selectGroupChildrenIds(state, group.id)))),
        shallowEqual,
    );
    const allNestedGroups = groups.filter((group) => nestedGroupIds.includes(group.id));
    const groupsToUpload = [...userGroups, ...allNestedGroups];

    const isOverlimit = useIsWorkspaceFeatureLimitReached("FileCountLimit");

    const defaultSelectedGroupId = cannotUploadFile && userGroups[0] ? userGroups[0].id : null;
    const selectedGroupId = userSelectedGroupId === undefined ? defaultSelectedGroupId : userSelectedGroupId;

    const noGroupSelected = cannotUploadFile && selectedGroupId === null;
    const noTarget = (!workspace && groupsToUpload.length === 0) || noGroupSelected;
    const disabledUpload = uploadStarted || isOverlimit || noTarget;

    const getTooltipTitle = () => {
        if (isOverlimit) {
            return t("pages:filesPage.tooltips.overlimit");
        }
        if (uploadStarted) {
            return t("pages:filesPage.tooltips.uploadInProgress");
        }
        if (noTarget) {
            return t("pages:filesPage.tooltips.noTarget");
        }
        return "";
    };

    const uploadTarget = getUploadTarget({
        selectedGroupId,
        groupsToUpload,
        groups,
    });

    const uploadTargetName = uploadTarget.groupName
        ? uploadTarget.groupName
        : uploadTarget.isWorkspace
          ? t("pages:filesPage.input.uploadField.workspaceLabel")
          : undefined;

    return (
        <PageContainer fitViewport>
            <PageHeader title={t("pages:filesPage.title")} description={t("pages:filesPage.description")} />
            <ExpiredWrapper>
                <div className={classes.pageLayout}>
                    {treeOpen && (
                        <GroupTree selectedGroupId={selectedGroupId} onSelect={handleGroupSelect} onToggle={handleTreeToggle} />
                    )}
                    <div className={classes.mainContent}>
                        <FileExplorerHeader
                            selectedGroupId={selectedGroupId}
                            includeParentFiles={includeParentFiles}
                            onIncludeParentFilesChange={setIncludeParentFiles}
                            onSelect={handleGroupSelect}
                            isSidebarCollapsed={!treeOpen}
                            onExpandSidebar={handleTreeToggle}
                        />

                        {uploadStarted ? <UploadProgressBar /> : null}
                        <UploadTable
                            selectedGroupId={selectedGroupId}
                            includeParentFiles={includeParentFiles}
                            dropDisabled={disabledUpload}
                            dropDisabledReason={getTooltipTitle()}
                            onUploadClick={() => setUploadDialogOpen(true)}
                        />

                        <FileUploadModal
                            open={uploadDialogOpen}
                            onClose={() => setUploadDialogOpen(false)}
                            groupId={uploadTarget.groupId}
                            groupName={uploadTargetName}
                            disabled={disabledUpload}
                            showProgressBar={false}
                            hint={t("modals:uploadFile.dragHint")}
                        />
                    </div>
                </div>
            </ExpiredWrapper>
        </PageContainer>
    );
};

const UploadPage = () => {
    return (
        <UploadProvider>
            <UploadPageContent />
        </UploadProvider>
    );
};

export default withMetaDescription(UploadPage, {
    title: i18next.t("pages:filesPage.metaTitle"),
    description: i18next.t("pages:filesPage.metaDescription"),
});
